/**
 * Main Product Page JavaScript
 * Handles initialization, re-initialization, and all PDP interactions
 * Designed to work with Section Rendering API swaps
 * 
 * Note: Variant selection and ATC validation are handled by:
 * - global.js (VariantRadios custom element)
 * - product-form.js (form submission and validation)
 */

(function() {
  'use strict';
  
  let galleryInstance = null;
  let isInitialized = false;
  
  /**
   * Main initialization function
   * Can be called on initial load and after Section Rendering API swaps
   */
  function initProductPage(root = document) {
    const section = root.querySelector('[data-section-id]') || root;
    const sectionId = section.dataset.sectionId;
    
    if (!sectionId) {
      console.warn('Product page: No section ID found');
      return;
    }
    
    // Initialize gallery
    initGallery(root);
    
    // Initialize size chart drawer
    initSizeChartDrawer(root);
    
    // Initialize sticky behaviors (desktop)
    initStickyBehaviors(root);
    
    // Initialize mobile sticky ATC bar
    initMobileStickyATC(root);
    
    // Initialize review summary anchor
    initReviewSummary(root);
    
    // Initialize complete the look
    initCompleteTheLook(root);
    
    // Rebind Okendo if needed
    rebindOkendo(root);
    
    isInitialized = true;
  }
  
  /**
   * Initialize gallery
   */
  function initGallery(root) {
    const galleryContainer = root.querySelector('[data-gallery-container]');
    if (galleryContainer && window.ProductGallery) {
      // Destroy existing instance if any (clears the galleryInitialized flag)
      if (galleryInstance) {
        galleryInstance.destroy();
      }
      // Skip if another init (auto-init) already claimed this container
      if (galleryContainer.dataset.galleryInitialized === 'true') {
        return;
      }
      galleryInstance = new window.ProductGallery(galleryContainer);
    }
  }
  
  /**
   * Initialize size chart drawer
   */
  function initSizeChartDrawer(root) {
    const trigger = root.querySelector('[data-size-chart-trigger]');

    function openSizeChart() {
      const contentDiv = document.querySelector('[data-size-chart-content]');
      if (contentDiv && window.openSizeChartDrawer) {
        window.openSizeChartDrawer(contentDiv.innerHTML);
      }
    }

    if (trigger) {
      trigger.addEventListener('click', openSizeChart);
    }

    if (window.location.hash === '#size-chart') {
      customElements.whenDefined('size-chart-drawer').then(openSizeChart);
    }

    window.addEventListener('hashchange', () => {
      if (window.location.hash === '#size-chart') {
        openSizeChart();
      }
    });
  }
  
  /**
   * Initialize sticky behaviors
   * Uses CSS sticky when gallery is taller, otherwise no-op
   * The trick: set gallery min-height to ensure it's always taller than buy-box
   * Only applies on desktop (769px+) - mobile uses single column layout
   */
  function initStickyBehaviors(root) {
    const buyBoxColumn = root.querySelector('.main-product__buy-box-column');
    const galleryColumn = root.querySelector('.main-product__gallery-column');
    if (!buyBoxColumn || !galleryColumn) return;
    
    // Skip in Shopify editor to prevent flickering
    if (window.Shopify && window.Shopify.designMode) return;
    
    // Function to ensure gallery is tall enough for sticky to work
    const ensureStickyWorks = () => {
      // Only apply on desktop breakpoint (769px+)
      if (window.innerWidth < 769) {
        // Reset any previously set min-height on mobile
        galleryColumn.style.minHeight = '';
        return;
      }
      
      const buyBoxHeight = buyBoxColumn.getBoundingClientRect().height;
      const galleryHeight = galleryColumn.getBoundingClientRect().height;
      
      // If gallery is shorter or equal, set min-height on gallery to be buyBox + scroll room
      if (galleryHeight <= buyBoxHeight) {
        // Add extra height so there's room to scroll (buyBox height + 200px buffer)
        const minHeight = buyBoxHeight + 200;
        galleryColumn.style.minHeight = `${minHeight}px`;
      }
    };
    
    // Run after images load to get accurate heights
    if (document.readyState === 'complete') {
      ensureStickyWorks();
    } else {
      window.addEventListener('load', ensureStickyWorks);
    }
    
    // Also run after a short delay to catch lazy-loaded images
    setTimeout(ensureStickyWorks, 1000);
    
    // Re-run on resize to handle breakpoint changes
    window.addEventListener('resize', ensureStickyWorks);
  }
  
  /**
   * Initialize mobile sticky ATC bar
   * Only shown when the main ATC is scrolled out of view AND the current variant is in stock.
   * When a variant is sold out (ATC disabled / Klaviyo BIS injected), the sticky bar stays hidden.
   */
  function initMobileStickyATC(root) {
    const stickyATC = root.querySelector('[data-sticky-atc-mobile]');
    if (!stickyATC) return;

    const mainATC = root.querySelector('[data-add-to-cart-button]');
    if (!mainATC) return;

    let isOutOfView = false;

    function updateVisibility() {
      if (isOutOfView && !mainATC.disabled) {
        stickyATC.classList.add('is-visible');
      } else {
        stickyATC.classList.remove('is-visible');
      }
    }

    const intersectionObserver = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          isOutOfView = !entry.isIntersecting;
          updateVisibility();
        });
      },
      { threshold: 0 }
    );
    intersectionObserver.observe(mainATC);

    const attrObserver = new MutationObserver(updateVisibility);
    attrObserver.observe(mainATC, { attributes: true, attributeFilter: ['disabled'] });

    const stickyButton = stickyATC.querySelector('[data-sticky-add-to-cart-button]');
    if (stickyButton) {
      stickyButton.addEventListener('click', function() {
        if (mainATC.disabled) {
          const productForm = root.querySelector('product-form');
          if (productForm && typeof productForm.handleErrorMessage === 'function') {
            productForm.handleErrorMessage(
              window.variantStrings?.selectSize || 'Select a size before adding to cart'
            );
          }
        }
        mainATC.click();
        mainATC.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
  
  /**
   * Initialize review summary anchor
   */
  function initReviewSummary(root) {
    const reviewLink = root.querySelector('[data-review-summary-link]');
    if (reviewLink) {
      reviewLink.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Find the section that contains Okendo reviews
        // Look for sections with Okendo review widgets
        const allSections = document.querySelectorAll('section[id^="shopify-section-"]');
        let reviewsSection = null;
        
        for (let section of allSections) {
          // Check if this section contains Okendo review content
          if (section.querySelector('[data-oke-widget], [data-oke-container], .okeReviews, div[data-oke-widget]')) {
            reviewsSection = section;
            break;
          }
        }
        
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // Fallback to #reviews-full if section not found
          const reviewsFull = document.querySelector('#reviews-full');
          if (reviewsFull) {
            reviewsFull.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    }
  }
  
  /**
   * Initialize complete the look
   * Quick view buttons are handled by the quick-view custom element
   * No additional initialization needed as onclick handlers are inline
   */
  function initCompleteTheLook(root) {
    // Quick view functionality is handled by the quick-view custom element
    // and inline onclick handlers, so no additional setup needed
  }
  
  /**
   * Rebind Okendo after DOM swaps
   */
  function rebindOkendo(root) {
    // Okendo should auto-initialize, but we can trigger a refresh if needed
    if (window.OkendoReviews && window.OkendoReviews.init) {
      window.OkendoReviews.init();
    }
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductPage();
    });
  } else {
    initProductPage();
  }
  
  // Export for re-initialization
  window.initProductPage = initProductPage;
  
})();
